import { useEffect, useMemo, useRef, useState } from "react";
import "./NovaPlayer.css";
/**
 * Sleek Web Music Player (React)
 * - Tek bileşen: <MusicPlayer tracks={[{title, artist, src, cover}]} />
 * - Özellikler: Oynat/Duraklat, Önceki/Sonraki, Karıştır, Tekrar, Ses, Seekbar, Kısayollar, MediaSession
 * - Yerel depolama: ses, son parça, karıştır/tekrar bayrakları
 */
export default function NovaPlayer({ tracks }) {
    const DEFAULTS = useMemo(() => ({
        volume: parseFloat(localStorage.getItem("player:volume") ?? "0.9"),
        index: parseInt(localStorage.getItem("player:index") ?? "0", 10),
        shuffle: localStorage.getItem("player:shuffle") === "1",
        repeat: localStorage.getItem("player:repeat") === "1",
    }), []);

    const FALLBACK_TRACKS = useMemo(() => (
        [
            {
                title: "Retro Drive",
                artist: "Lodos Studio",
                src: "https://prod.apiollon.com/spotify/rock-sample.mp3",
                cover: "",
            },
            {
                title: "Floating Lights",
                artist: "Vertex",
                src: "https://cdn.pixabay.com/download/audio/2022/10/25/audio_7b558d3cde.mp3?filename=higher-121386.mp3",
                cover: "",
            },
            {
                title: "Morning Dew",
                artist: "OpenSound",
                src: "https://cdn.pixabay.com/download/audio/2022/03/15/audio_1a3ab8f1db.mp3?filename=lofi-study-112191.mp3",
                cover: "",
            },
            {
                title: "Retro Drive2",
                artist: "Lodos Studio",
                src: "https://prod.apiollon.com/spotify/rock-sample.mp3",
                cover: "",
            },
            {
                title: "Floating Lights2",
                artist: "Vertex",
                src: "https://cdn.pixabay.com/download/audio/2022/10/25/audio_7b558d3cde.mp3?filename=higher-121386.mp3",
                cover: "",
            },
            {
                title: "Morning Dew2",
                artist: "OpenSound",
                src: "https://cdn.pixabay.com/download/audio/2022/03/15/audio_1a3ab8f1db.mp3?filename=lofi-study-112191.mp3",
                cover: "",
            },
        ]
    ), []);

    const list = tracks?.length ? tracks : FALLBACK_TRACKS;

    // ===== Refs & State =====
    const audioRef = useRef(null);
    const seekRef = useRef(null);
    const urlInputRef = useRef(null);

    const [index, setIndex] = useState(Math.max(0, Math.min(DEFAULTS.index, list.length - 1)));
    const [shuffle, setShuffle] = useState(DEFAULTS.shuffle);
    const [repeat, setRepeat] = useState(DEFAULTS.repeat);
    const [isSeeking, setIsSeeking] = useState(false);
    const [volume, setVolume] = useState(Math.max(0, Math.min(DEFAULTS.volume, 1)));
    const [muted, setMuted] = useState(false);
    const [curTime, setCurTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showList, setShowList] = useState(true);
    const [playlist, setPlaylist] = useState([...list]);
    const [toast, setToast] = useState("");

    const track = playlist[index] ?? { title: "—", artist: "—", src: "", cover: "" };

    // ===== Effects =====
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.volume = volume;
    }, [volume]);

    useEffect(() => {
        localStorage.setItem("player:index", String(index));
    }, [index]);

    useEffect(() => {
        localStorage.setItem("player:shuffle", shuffle ? "1" : "0");
    }, [shuffle]);

    useEffect(() => {
        localStorage.setItem("player:repeat", repeat ? "1" : "0");
    }, [repeat]);

    // Media Session + keyboard
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // MediaSession
        if ("mediaSession" in navigator) {
            navigator.mediaSession.metadata = new window.MediaMetadata({
                title: track.title,
                artist: track.artist,
            });
            navigator.mediaSession.setActionHandler("play", handlePlay);
            navigator.mediaSession.setActionHandler("pause", handlePause);
            navigator.mediaSession.setActionHandler("previoustrack", prev);
            navigator.mediaSession.setActionHandler("nexttrack", next);
            navigator.mediaSession.setActionHandler("seekto", (d) => {
                if (d.seekTime != null) audio.currentTime = d.seekTime;
            });
        }

        const onKey = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            switch (e.key) {
                case " ":
                    e.preventDefault();
                    toggle();
                    break;
                case "ArrowRight":
                    audio.currentTime = Math.min((audio.currentTime || 0) + 5, audio.duration || 0);
                    break;
                case "ArrowLeft":
                    audio.currentTime = Math.max((audio.currentTime || 0) - 5, 0);
                    break;
                case "m":
                case "M":
                    setMuted((m) => !m);
                    break;
                case "l":
                case "L":
                    setRepeat((r) => !r);
                    break;
                case "s":
                case "S":
                    setShuffle((s) => !s);
                    break;
                default:
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [track]);

    // audio tag event handlers
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onTime = () => {
            if (!isSeeking) {
                setCurTime(audio.currentTime || 0);
                setDuration(audio.duration || 0);
                if (seekRef.current) {
                    const pct = (audio.currentTime / (audio.duration || 1)) * 100;
                    seekRef.current.value = String(pct);
                    seekRef.current.style.setProperty("--progress", `${pct}%`);
                }
            }
        };
        const onLoaded = () => setDuration(audio.duration || 0);
        const onEnded = () => {
            if (repeat) {
                audio.currentTime = 0;
                audio.play().catch(() => { });
            } else {
                next();
            }
        };

        const onError = () => showToast("Ses kaynağı yüklenemedi.");

        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onLoaded);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("error", onError);

        return () => {
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onLoaded);
            audio.removeEventListener("ended", onEnded);
            audio.removeEventListener("error", onError);
        };
    }, [isSeeking, repeat]);

    // keep muted -> audio
    useEffect(() => {
        if (audioRef.current) audioRef.current.muted = muted || volume === 0;
    }, [muted, volume]);

    // ===== Helpers =====
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const fmtTime = (sec) => {
        if (!Number.isFinite(sec)) return "0:00";
        const s = Math.floor(sec % 60).toString().padStart(2, "0");
        const m = Math.floor(sec / 60);
        return `${m}:${s}`;
    };
    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 1500);
    };

    // ===== Transport =====
    const loadIndex = (i) => {
        setIndex(clamp(i, 0, playlist.length - 1));
    };
    const handlePlay = () => audioRef.current?.play().catch(() => showToast("Çalınamadı. CORS veya ağ engeli olabilir."));
    const handlePause = () => audioRef.current?.pause();
    const toggle = () => (audioRef.current?.paused ? handlePlay() : handlePause());
    const prev = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.currentTime > 3) {
            audio.currentTime = 0;
        } else {
            if (shuffle) {
                let r = 0;
                if (playlist.length > 1) {
                    do {
                        r = Math.floor(Math.random() * playlist.length);
                    } while (r === index);
                }
                loadIndex(r);
            } else {
                loadIndex(index - 1 < 0 ? playlist.length - 1 : index - 1);
            }
        }
        handlePlay();
    };
    const next = () => {
        if (shuffle) {
            let r = 0;
            if (playlist.length > 1) {
                do {
                    r = Math.floor(Math.random() * playlist.length);
                } while (r === index);
            }
            loadIndex(r);
        } else {
            loadIndex(index + 1 >= playlist.length ? 0 : index + 1);
        }
        handlePlay();
    };

    // track change -> set src & load
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.src = track.src || "";
        audio.load();
        // store current index
        localStorage.setItem("player:index", String(index));
    }, [index, track.src]);

    // ===== Seek handlers =====
    const onSeekInput = (e) => {
        const pct = parseFloat(e.target.value || "0");
        setIsSeeking(true);
        if (seekRef.current) seekRef.current.style.setProperty("--progress", `${pct}%`);
        setCurTime((pct / 100) * (duration || 0));
    };
    const onSeekCommit = (e) => {
        const pct = parseFloat(e.target.value || "0");
        const audio = audioRef.current;
        if (audio) audio.currentTime = (pct / 100) * (audio.duration || 0);
        setIsSeeking(false);
    };

    // ===== Playlist actions =====
    const addByUrl = () => {
        const url = urlInputRef.current?.value.trim();
        if (!url) return;
        try {
            const u = new URL(url);
            const title = u.pathname.split("/").pop() || "Yeni Parça";
            setPlaylist((p) => [...p, { title, artist: "URL", src: url, cover: "" }]);
            urlInputRef.current.value = "";
            showToast("Listeye eklendi");
        } catch (e) {
            showToast("Geçersiz URL");
        }
    };

    // ===== UI =====
    return (
        <div className="player-root">
            <main className="player" role="region" aria-label="Müzik oynatıcı">
                {/* LEFT */}
                <section>
                    <div className="cover" aria-label="Albüm kapağı">
                        <span className="badge">{isPlaying ? "Çalıyor" : "Duraklatıldı"}</span>
                        {track.cover ? (
                            <img src={track.cover} alt="Kapak görseli" />
                        ) : (
                            <svg viewBox="0 0 360 360" width="100%" height="100%">
                                <defs>
                                    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0" stopColor="var(--accent)" />
                                        <stop offset="1" stopColor="var(--accent-2)" />
                                    </linearGradient>
                                </defs>
                                <rect width="360" height="360" fill="url(#g)" />
                                <g fill="rgba(255,255,255,.85)">
                                    <circle cx="120" cy="180" r="16" />
                                    <rect x="160" y="132" width="26" height="96" rx="6" />
                                    <rect x="200" y="152" width="26" height="76" rx="6" />
                                </g>
                            </svg>
                        )}
                    </div>
                    <div className="meta">
                        <div className="title">{track.title || "—"}</div>
                        <div className="artist">{track.artist || "—"}</div>
                    </div>
                </section>

                {/* RIGHT */}
                <section className="right">
                    <div className="controls" aria-label="Oynatma kontrolleri">
                        <div className="row">
                            <div className="time">{fmtTime(curTime)}</div>
                            <input
                                ref={seekRef}
                                id="seek"
                                type="range"
                                min="0"
                                max="100"
                                step="0.1"
                                defaultValue="0"
                                onInput={onSeekInput}
                                onChange={onSeekCommit}
                                aria-label="İlerleme çubuğu"
                            />
                            <div className="time">{fmtTime(duration)}</div>
                        </div>
                        <div className="transport">
                            <button className={`btn ${shuffle ? "toggled" : ""}`} onClick={() => { setShuffle((s) => !s); showToast(!shuffle ? "Karıştır: Açık" : "Karıştır: Kapalı"); }} aria-label="Karıştır">
                                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M17 3h4v4h-2V5h-2V3Zm0 18v-2h2v-2h2v4h-4ZM3 5h6.17l3.41 3.41l-1.42 1.42L8.59 7H3V5Zm0 14v-2h5.59l2.7-2.7l1.41 1.42L9.41 19H3Zm14.59-6H21v2h-2.59l-3 3l-1.41-1.41L17.59 13Zm0-2L14 6.83L15.41 5.4l3 3H21v2h-3.41Z" /></svg>
                            </button>
                            <button className="btn" onClick={prev} aria-label="Önceki">
                                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M6 6h2v12H6V6Zm3.5 6l8.5 6V6l-8.5 6Z" /></svg>
                            </button>
                            <button className="btn primary" onClick={toggle} aria-label="Oynat/Duraklat">
                                {isPlaying ? (
                                    <svg viewBox="0 0 24 24"><path fill="currentColor" d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
                                ) : (
                                    <svg viewBox="0 0 24 24"><path fill="currentColor" d="M8 5v14l11-7z" /></svg>
                                )}
                            </button>
                            <button className="btn" onClick={next} aria-label="Sonraki">
                                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M6 6v12l8.5-6L6 6Zm10 0h2v12h-2V6Z" /></svg>
                            </button>
                            <button className={`btn ${repeat ? "toggled" : ""}`} onClick={() => { setRepeat((r) => !r); showToast(!repeat ? "Tekrar: Açık" : "Tekrar: Kapalı"); }} aria-label="Tekrarla">
                                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M7 7h8V4l5 4l-5 4V9H7a4 4 0 0 0 0 8h3v2H7a6 6 0 1 1 0-12Z" /></svg>
                            </button>
                        </div>
                        <div className="extras">
                            <div className="vol">
                                <button className={`btn ${muted || volume === 0 ? "toggled" : ""}`} onClick={() => setMuted((m) => !m)} aria-label="Sessize al">
                                    {muted || volume === 0 ? (
                                        <svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 9v6h4l5 4V5L8 9H4zm12.59-3L20 9.41L17.41 12L20 14.59L18.59 16L16 13.41L13.41 16L12 14.59L14.59 12L12 9.41L13.41 8L16 10.59L18.59 8z" /></svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 9v6h4l5 4V5L8 9H4z" /></svg>
                                    )}
                                </button>
                                <input
                                    id="volume"
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={volume}
                                    aria-label="Ses seviyesi"
                                    onChange={(e) => {
                                        const v = parseFloat(e.target.value);
                                        setVolume(v);
                                        localStorage.setItem("player:volume", String(v));
                                    }}
                                />
                                <span className="label">%{Math.round((volume || 0) * 100)}</span>
                            </div>
                            <div className="spacer" />
                            <button className={`btn ${showList ? "toggled" : ""}`} onClick={() => setShowList((v) => !v)} aria-label="Çalma listesini göster/gizle">
                                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 6h14v2H3V6Zm0 5h14v2H3v-2Zm0 5h10v2H3v-2Zm16-7h2v8h-2v-8Z" /></svg>
                                Liste
                            </button>
                        </div>
                    </div>

                    {/* PLAYLIST */}
                    {showList && (
                        <div className="playlist" aria-label="Çalma listesi">
                            <div className="pl-head">
                                <div className="pl-title">Çalma Listesi</div>
                                <div className="hint">
                                    Kısayollar: <span className="kbd">Space</span> oynat/duraklat, <span className="kbd">←/→</span> sar, <span className="kbd">M</span> sessiz
                                </div>
                            </div>
                            <div className="add-url">
                                <input ref={urlInputRef} type="url" placeholder="MP3 URL yapıştır (CORS politikalarına dikkat)" />
                                <button className="btn" onClick={addByUrl}>Ekle</button>
                            </div>
                            <ul className="tracks" role="list">
                                {playlist.map((t, i) => (
                                    <li key={i} className={`track ${i === index ? "active" : ""}`} role="listitem" onClick={() => { setIndex(i); handlePlay(); }}>
                                        <div className="idx">{String(i + 1).padStart(2, "0")}</div>
                                        <div className="tmeta">
                                            <div className="t">{t.title}</div>
                                            <div className="a">{t.artist}</div>
                                        </div>
                                        <div className="tdur">{/* süre, parça yüklendiğinde üstte gösteriliyor */}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            </main>
            {/* toast */}
            {toast && <div className="toast" role="status" aria-live="polite">{toast}</div>}
            <audio ref={audioRef} crossOrigin="anonymous" />
        </div>
    );
}
